import { Grid, SpaceBetween } from '@amzn/awsui-components-react';
import React from 'react';

import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { AnnualCostsChart, CumulativeCostsChart } from './CashflowCharts';
import { getMainframeCashflowCostColumnDefinitions } from './CashflowColumnDefinitions';
import { CashflowRow, CashflowTables, CashflowTabProps, MainframeCashflow, SummaryRowNumberDisplayOptions } from './MainframeTypes';

const cashflowTable = (duration: number, cashflowRows: CashflowTables) => {
    const columnDefinitions = getMainframeCashflowCostColumnDefinitions(duration);
    // Using a custom dummy filler to separate between the 3 logical areas of the table.
    const filler: CashflowRow = {
        cashflowValues: new Array(cashflowRows.costs.length).fill(''),
        description: ' ',
    };

    const items = [...cashflowRows.costs, filler, ...cashflowRows.totalCosts, filler, ...cashflowRows.cummulativeCosts];
    return (
        <div>
            <AWSCATTable
                resourceName={constants.MIGRATION_CASH_FLOW_HEADER}
                columnDefinitions={columnDefinitions}
                items={items}
                defaultPageSize={25}
            />
        </div>
    );
};

const cashflowToRows = (
    currency: string,
    cashflow: MainframeCashflow,
): CashflowTables => {
    return {
        costs: [
            {
                cashflowValues: cashflow.aws,
                currency,
                description: constants.AWS_COST,
            },
            {
                cashflowValues: cashflow.onPrem,
                currency,
                description: constants.ON_PREM_COST,
            },
            {
                cashflowValues: cashflow.migration,
                currency,
                description: constants.MIGRATION_COST,
            },
        ],
        cummulativeCosts: [
            {
                cashflowValues: cashflow.cumulativeMigrateToAWS,
                currency,
                description: constants.CUMULATIVE_AWS_MIGRATION,
            },
            {
                cashflowValues: cashflow.cumulativeStayOnPrem,
                currency,
                description: constants.CUMULATIVE_NO_CHANGE,
            },
            {
                cashflowValues: cashflow.cumulativeDifference,
                currency,
                description: constants.CUMULATIVE_DIFFERENCE,
            },
            {
                cashflowValues: cashflow.cumulativeSavings,
                description: constants.CUMULATIVE_SAVINGS,
                format: {
                    numberDisplay: SummaryRowNumberDisplayOptions.PERCENTAGE,
                },
            },
        ],
        totalCosts: [
            {
                cashflowValues: cashflow.totalMigrateToAWS,
                currency,
                description: constants.TOTAL_AWS_MIGRATION,
            },
            {
                cashflowValues: cashflow.totalStayOnPrem,
                currency,
                description: constants.TOTAL_NO_CHANGE,
            },
            {
                cashflowValues: cashflow.totalDifference,
                currency,
                description: constants.TOTAL_DIFFERENCE,
            },
            {
                cashflowValues: cashflow.totalSavings,
                description: constants.TOTAL_SAVINGS,
                format: {
                    numberDisplay: SummaryRowNumberDisplayOptions.PERCENTAGE,
                },
            },
        ],
    };
};

function CashflowTab({ currency, cashflow}: CashflowTabProps) {
    if (!cashflow?.duration) {
        return null;
    } else {
        const cashflowRows = cashflowToRows(currency, cashflow);
        return (
            <div>
                <SpaceBetween direction='vertical' size='m'>
                    {cashflowTable(cashflow.duration, cashflowRows)}
                    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                        <div>{CumulativeCostsChart(currency, cashflow)}</div>
                        <div>{AnnualCostsChart(currency, cashflow)}</div>
                    </Grid>
                </SpaceBetween>
            </div>
        );
    }
}

export default CashflowTab;
